import React from 'react'
import './history-log-fall-2023.css'

const formatStatusHistoryDate = (value) => {
    if (!value) return ''

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const formatHistoryTitle = (log) => {
    const subject = log.entityType === 'student'
        ? (log.studentName || 'Student')
        : ''

    if (log.previousStatus) {
        return subject
            ? `${subject}: ${log.previousStatus} -> ${log.status}`
            : `${log.previousStatus} -> ${log.status}`
    }

    return subject
        ? `${subject}: ${log.status}`
        : `${log.status}`
}

const formatTeacherAssignmentTitle = (log) => {
    return log.studentName || 'Student'
}

const formatTeacherAssignmentReason = (reason) => {
    if (!reason) return ''

    return reason
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

const formatCompactTeacherName = (name) => {
    const trimmedName = String(name || '').trim()

    if (!trimmedName) {
        return 'Unassigned'
    }

    const nameParts = trimmedName.split(/\s+/).filter(Boolean)

    if (nameParts.length === 1) {
        return nameParts[0]
    }

    const [firstName, lastName] = nameParts
    return `${firstName} ${lastName.charAt(0)}.`
}

const buildTeacherImageUrl = (imageName) => {
    if (!imageName) return ''

    return imageName.startsWith('http')
        ? imageName
        : `https://app.atharo.app/images/${imageName}`
}

const TeacherSwitchAvatar = ({ name, imageName }) => {
    const firstLetter = String(name || '?').trim().charAt(0).toUpperCase() || '?'
    const imageUrl = buildTeacherImageUrl(imageName)

    if (imageUrl) {
        return (
            <img
                alt={name || 'Teacher'}
                className='history-log-fall-2023__teacher-avatar'
                src={imageUrl}
            />
        )
    }

    return (
        <div className='history-log-fall-2023__teacher-avatar history-log-fall-2023__teacher-avatar--fallback'>
            {firstLetter}
        </div>
    )
}

const HistoryLogFall2023 = ({ log, entryType = 'history', teacherImageMap = {} }) => {
    const isTeacherAssignmentHistory = entryType === 'teacher-assignment-history'
    const showReason = isTeacherAssignmentHistory
        ? Boolean(log.reason)
        : log.status === 'Inquired' && log.reason
    const historyDate = isTeacherAssignmentHistory
        ? (log.changedAt || log.date)
        : (log.effectiveDate || log.date)
    const cardClassName = isTeacherAssignmentHistory
        ? 'history-log-fall-2023 history-log-fall-2023--teacher-switch'
        : 'history-log-fall-2023'
    const reasonText = isTeacherAssignmentHistory
        ? formatTeacherAssignmentReason(log.reason)
        : log.reason
    const titleText = isTeacherAssignmentHistory
        ? formatTeacherAssignmentTitle(log)
        : formatHistoryTitle(log)
    const noteText = isTeacherAssignmentHistory
        ? (log.notes || '')
        : (log.note || '')
    const previousTeacherImageName = teacherImageMap[String(log.previousTeacherId || '')] || ''
    const newTeacherImageName = teacherImageMap[String(log.newTeacherId || '')] || ''
    const studentNameText = log.studentName || 'Student'
    const previousTeacherLabel = formatCompactTeacherName(log.previousTeacherName)
    const newTeacherLabel = formatCompactTeacherName(log.newTeacherName)

    return (
        <div className={cardClassName}>
            <div className='history-log-fall-2023__top-row'>
                <div className='history-log-fall-2023__date'>{formatStatusHistoryDate(historyDate)}</div>
            </div>
            {showReason && <div className='history-log-fall-2023__reason'>{reasonText}</div>}
            {isTeacherAssignmentHistory && (
                <div className='history-log-fall-2023__teacher-switch-row history-log-fall-2023__teacher-switch-row--inline'>
                    <div className='history-log-fall-2023__title history-log-fall-2023__title--inline'>{studentNameText}</div>
                    <div className='history-log-fall-2023__teacher-switch-person'>
                        <TeacherSwitchAvatar name={log.previousTeacherName || 'Previous Teacher'} imageName={previousTeacherImageName} />
                        <div className='history-log-fall-2023__teacher-switch-name'>{previousTeacherLabel}</div>
                    </div>
                    <div className='history-log-fall-2023__teacher-switch-arrow'>→</div>
                    <div className='history-log-fall-2023__teacher-switch-person'>
                        <TeacherSwitchAvatar name={log.newTeacherName || 'New Teacher'} imageName={newTeacherImageName} />
                        <div className='history-log-fall-2023__teacher-switch-name'>{newTeacherLabel}</div>
                    </div>
                </div>
            )}
            {!isTeacherAssignmentHistory && <div className='history-log-fall-2023__title'>{titleText}</div>}
            {noteText && <div className='history-log-fall-2023__note'>{noteText}</div>}
        </div>
    )
}

export default HistoryLogFall2023
